import { Component } from '@angular/core';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { ClassicEditor } from '@/common/editor/classic-editor';

import { ShowComponent } from 'src/app/common/component/show.component';
import { DateService } from 'src/app/common/date.service';
import { FirstDayDateAdapter } from 'src/app/common/first-day-date-adapter';
import {
	concat,
	debounceTime,
	distinctUntilChanged,
	filter,
	Observable,
	Observer,
	of,
	Subject,
	switchMap,
	tap,
} from 'rxjs';

export interface Product {
	id: number;
	productKind: ProductKind;
	name: string;
	status: string;
	code: string;
	location: Location[];
	tags: Tag[];
	main_picture: MainPicture;
	nights_stay: number;
}

export interface ProductKind {
	id: number;
	name: string;
}

export interface Location {
	id: number;
	name: string;
	code: string;
	locationKind: string;
	parentLocation: string;
}

export interface Tag {
	id: number;
	name: string;
	tag: string;
}

export interface MainPicture {
	id: number;
	multimedia: Multimedia;
	order: number;
}

export interface Multimedia {
	id: string;
	name: string;
	url: string;
}

@Component({
	selector: 'app-promotions',
	templateUrl: './promotions.component.html',
	styleUrls: ['./promotions.component.css'],
	providers: [
		{ provide: MAT_DATE_LOCALE, useValue: 'pl-PL' },
		{ provide: DateAdapter, useClass: FirstDayDateAdapter },
	],
})
export class PromotionsComponent extends ShowComponent {
	protected dateService = new DateService();
	protected apiType = 'bag';
	protected apiUrlAdd = '/offer/promotion/';
	protected itemsBeforeDataLoad = 11;
	pageTitle = 'Promocje';
	protected parentComponent = 'promotions';
	editor = ClassicEditor;
	editorConfig = {
		toolbar: ['bold', 'italic', '|', 'bulletedList', 'numberedList'],
	};
	data = {
		id: null,
		name: '',
		catalog: null,
		description: '',
		isActive: false,
		manual: false,
		countAutomatically: false,
		promotionSells: [],
		promotionOffers: [],
		promotionRules: [],
		accommodationNights: {
			upper: null,
			lower: null,
			bounds: '[]',
		},
		importance: null,
		method: 'P',
		sellChannels: [],
		sellCategories: [],
		organizations: [],
		stops: [],
		transports: [],
		boards: [],
		kinds: [],
		products: [],
		priceRange: {
			upper: null,
			lower: null,
			bounds: '[]',
		},
		methodValue: 0,
		payAdvTermMethod: null,
		payAdvTermValue: null,
		payAdvTermDate: null,
		payAdvCountKind: null,
		payAdvCountValue: null,
		payFullTermMethod: 'B',
		payFullTermValue: 30,
		payFullTermDate: null,
		allowedPromotions: [],
	};
	catalogs: Array<any> = [];
	promotions: Array<any> = [];
	promotionWhitelist: Array<any> = [];
	promotionBlacklist: Array<any> = [];
	destinations: Array<any> = [];
	destinationWhitelist: Array<any> = [];
	destinationBlacklist: Array<any> = [];
	serviceTypes: Array<any> = [];
	countServicesBlacklist: Array<any> = [];
	countServicesWhitelist: Array<any> = [];
	productsWhitelist: Array<any> = [];
	productsBlacklist: Array<any> = [];
	services: Array<any> = [];
	servicesBlacklist: Array<any> = [];
	servicesWhitelist: Array<any> = [];
	channels: Array<any> = [];
	sellCategories: [];
	organizations: [];
	stops: [];
	boards: [];
	kinds: [];
	products: [];
	transportTypes: [];
	isActiveOptions = [
		{ enum: false, name: 'Nieaktywna' },
		{ enum: true, name: 'Aktywna' },
	];
	termMethod = [
		{ enum: 'B', name: 'Liczba dni przed wyjazdem' },
		{ enum: 'D', name: 'Konkretna data' },
		{ enum: 'A', name: 'Liczba dni po rezerwacji' },
	];
	promoMethod = [
		{ enum: 'V', name: 'Wartość' },
		{ enum: 'P', name: 'Procent' },
	];
	payAdvMethod = [
		{ enum: 'V', name: 'Wartość' },
		{ enum: 'P', name: 'Procent' },
	];

	protected products$: Observable<Product[]>;
	protected productLoading = false;
	protected productsInput$ = new Subject<string>();
	protected minLengthProduct = 3;

	protected getApiUrl(): string {
		return '/offer/promotion/' + this.data.id + '/';
	}

	protected loadBefore(): void {
		this.bag.get('/offer/catalog/', (result, error) => {
			this.catalogs = result.results;
			this.loadData();
		});
		this.bag.get('/offer/promotion/', (result, error) => {
			this.promotions = result.results;
			this.loadData();
		});
		this.bag.get('/reservation/channel/', (result, error) => {
			this.channels = result.results;
			this.loadData();
		});
		this.bag.get('/sell_category/', (result, error) => {
			this.sellCategories = result.results;
			this.loadData();
		});
		this.bag.get('/organization/', (result, error) => {
			this.organizations = result.results;
			this.loadData();
		});
		this.bag.get('/transport/stop/', (result, error) => {
			this.stops = result.results;
			this.loadData();
		});
		this.bag.get('/board/', (result, error) => {
			this.boards = result.results;
			this.loadData();
		});
		this.bag.get('/product/kind/', (result, error) => {
			this.kinds = result.results;
			this.loadData();
		});
		this.bag.get('/location/?limit=9999', (result, error) => {
			this.destinations = result.results;
			this.loadData();
		});
		this.bag.get('/offer/service/', (result, error) => {
			this.services = result.results;
			this.loadData();
		});
		this.bag.get('/offer/service_type/', (result, error) => {
			this.serviceTypes = result.results;
			this.loadData();
		});
		this.bag.get('/transport/transport_kinds/', (result, error) => {
			this.transportTypes = result.results;
			this.loadData();
		});
	}

	private loadProducts() {
		const productIdsArray = [...new Set(this.productsWhitelist.concat(this.productsBlacklist))];
		this.bag.get(`/product/?id__in=${[productIdsArray]}`, (result: { results: Product[] }, error) => {
			if (result) {
				this.products$ = this.loadSearchedProducts(result.results);
			}
		});
	}

	private loadSearchedProducts(defaultProduct: Product[]): Observable<Product[]> {
		return concat(
			of(defaultProduct),
			this.productsInput$.pipe(
				filter((res) => {
					return res !== null && res.length >= this.minLengthProduct;
				}),
				distinctUntilChanged(),
				debounceTime(800),
				tap(() => (this.productLoading = true)),
				switchMap((name) => {
					return this.getProducts(`name__icontains=${name}`).pipe(tap(() => (this.productLoading = false)));
				}),
			),
		);
	}

	private getProducts(params: string) {
		return new Observable((observer: Observer<Product[]>) => {
			this.bag.get(`/product/?${params}`, (result, error) => {
				if (error) {
					observer.error(error);
				} else {
					observer.next(result.results);
					observer.complete();
				}
			});
		});
	}

	protected afterLoadData() {
		this.fix_postgres_ranges(this.data.priceRange);
		this.fix_postgres_ranges(this.data.accommodationNights);

		this.destinationWhitelist = this.map_promotion_rules_to_array(this.data.promotionRules, 'LOCATION', 'W');
		this.destinationBlacklist = this.map_promotion_rules_to_array(this.data.promotionRules, 'LOCATION', 'B');
		this.countServicesWhitelist = this.map_promotion_rules_to_array(this.data.promotionRules, 'COUNT_SERVICE', 'W');
		this.countServicesBlacklist = this.map_promotion_rules_to_array(this.data.promotionRules, 'COUNT_SERVICE', 'B');
		this.servicesWhitelist = this.map_promotion_rules_to_array(this.data.promotionRules, 'SERVICE', 'W');
		this.servicesBlacklist = this.map_promotion_rules_to_array(this.data.promotionRules, 'SERVICE', 'B');
		this.productsWhitelist = this.map_promotion_rules_to_array(this.data.promotionRules, 'PRODUCT', 'W');
		this.productsBlacklist = this.map_promotion_rules_to_array(this.data.promotionRules, 'PRODUCT', 'B');
		this.loadProducts();
	}

	submitBefore(): void {
		this.check_null_range(this.data.accommodationNights);
		this.check_null_range(this.data.priceRange);

		this.data.promotionRules = this.create_promoton_rules(this.destinationWhitelist, 'W', 'LOCATION');

		this.data.promotionRules = this.data.promotionRules.concat(
			this.create_promoton_rules(this.destinationBlacklist, 'B', 'LOCATION'),
		);
		this.data.promotionRules = this.data.promotionRules.concat(
			this.create_promoton_rules(this.countServicesWhitelist, 'W', 'COUNT_SERVICE'),
		);
		this.data.promotionRules = this.data.promotionRules.concat(
			this.create_promoton_rules(this.countServicesBlacklist, 'B', 'COUNT_SERVICE'),
		);
		this.data.promotionRules = this.data.promotionRules.concat(
			this.create_promoton_rules(this.servicesWhitelist, 'W', 'SERVICE'),
		);
		this.data.promotionRules = this.data.promotionRules.concat(
			this.create_promoton_rules(this.servicesBlacklist, 'B', 'SERVICE'),
		);
		this.data.promotionRules = this.data.promotionRules.concat(
			this.create_promoton_rules(this.productsWhitelist, 'W', 'PRODUCT'),
		);
		this.data.promotionRules = this.data.promotionRules.concat(
			this.create_promoton_rules(this.productsBlacklist, 'B', 'PRODUCT'),
		);
		this.data.promotionOffers.forEach((element) => {
			element.offer.lower = this.fixDateFromRange(element.offer.lower);
			element.offer.upper = this.fixDateFromRange(element.offer.upper);
			element.offer.bounds = '[]';
		});
		this.data.promotionSells.forEach((element) => {
			element.sell.lower = this.fixDateFromRange(element.sell.lower);
			element.sell.upper = this.fixDateFromRange(element.sell.upper);
			element.sell.bounds = '[]';
		});
	}
	private fixDateFromRange(el) {
		if (typeof el == 'object' && el) {
			return this.dateService.toUtcDate(el);
		}
		return el;
	}

	private create_promoton_rules(array, rule, entity_name) {
		if (!array) {
			return [];
		}
		return array.map((entity_id) => {
			return {
				rule: rule,
				entityName: entity_name,
				entityId: entity_id,
			};
		});
	}
	private fix_postgres_ranges(range) {
		if (range.upper) {
			range.upper -= 1;
		}
		range.bounds = '[]';
	}

	private check_null_range(obj) {
		if (!obj.lower) {
			delete obj.lower;
		}
		if (!obj.upper) {
			delete obj.upper;
		}
	}

	private map_promotion_rules_to_array(array, type, rule) {
		if (!array) {
			return [];
		}
		return array
			.map((promotion) => {
				if (promotion.entityName != type) {
					return false;
				}
				if (promotion.rule != rule) {
					return false;
				}
				return promotion.entityId;
			})
			.filter((obj) => obj != false);
	}

	add_promotion_sell() {
		if (this.editable) {
			this.data.promotionSells.push({
				sell: {
					lower: null,
					upper: null,
					bounds: '[)',
				},
			});
		}
	}

	delete_promotion_sell(index) {
		if (this.editable) {
			this.data.promotionSells.splice(index, 1);
		}
	}

	add_promotion_offer() {
		if (this.editable) {
			this.data.promotionOffers.push({
				offer: {
					lower: null,
					upper: null,
					bounds: '[)',
				},
			});
		}
	}

	delete_promotion_offer(index) {
		if (this.editable) {
			this.data.promotionOffers.splice(index, 1);
		}
	}

	protected pageTitleGenerate(): string {
		return this.data.name + ' (ID: ' + this.data.id + ')';
	}

	duplicate() {
		this.pageFunction = 'add';
		this.data.name += ' (duplikat)';
		this.data.isActive = false;
		this.submit();
	}
	goToLink(url: string) {
		window.open(url, '_blank');
	}

	convertPriceByKind(value: number, kind: string): number {
		if (kind === 'V') return value * 10;
		return value / 10;
	}

	convertPriceToGrosze(price) {
		return price * 100;
	}

	convertPriceToPermill(price) {
		return price * 10;
	}

	checkSubmit() {
		for (let i = 0; i < this.data.promotionOffers.length; i++) {
			if (!this.data.promotionOffers[i].offer.lower || !this.data.promotionOffers[i].offer.upper) {
				this.infobox.error('Wprowadzono złą date w sekcji "Okres ofert"');
				return;
			}
		}
		for (let j = 0; j < this.data.promotionSells.length; j++) {
			if (!this.data.promotionSells[j].sell.lower || !this.data.promotionSells[j].sell.upper) {
				this.infobox.error('Wprowadzono złą date w sekcji "Okres ofert"');
				return;
			}
		}
		this.submit();
	}
}
